import { ConsentTracking, ContactStatus, UnsubscribedContactsOrderBy } from '@dev/ee-api/ee-api-models';
import { Reducer } from 'redux';

import dayjs from '@/services/tools/dayjs';
import { DEFAULT_ACTIVE_PAGE, DEFAULT_PER_PAGE_COUNT, LONG_DATE_FORMAT } from '@/services/constants';

import { ContactsActionTypes, ContactsInterface } from './types';

export const ContactsInitialState: ContactsInterface = {
    isContactsLoading: false,
    collection: null,
    selectedContact: null,
    contactPattern: null,
    validationStatus: {
        ValidationStatus: null,
        Contacts: 0,
        NewContacts: 0,
        validationStatus: null,
    },
    encodingName: null,
    postContactsParams: {
        listName: '0',
        body: [],
        status: ContactStatus.Active,
        consentTracking: ConsentTracking.Unknown,
    },
    isShowingUploadConfirmation: false,
    contactsOverview: null,
    isCreatingContacts: false,
    isContactHistoryLoading: false,
    contactHistory: null,
    isUploading: false,
    unsubscribedContacts: {
        overview: null,
        loadingUnsubscribedOverview: false,
        contactsList: [],
        pagination: {
            activePage: DEFAULT_ACTIVE_PAGE,
            email: null,
            from: null,
            perPage: DEFAULT_PER_PAGE_COUNT,
            statuses: [ContactStatus.Abuse, ContactStatus.Unsubscribed],
            to: dayjs().endOf('day').format(LONG_DATE_FORMAT),
            orderBy: UnsubscribedContactsOrderBy.DateDesc,
        },
        loadingUnsubscribed: false,
        statusCount: null,
        pickedContactEmail: null,
        unsubReasons: null,
    },
    hasMoreContacts: true,
};

const reducer: Reducer<ContactsInterface> = (
    state: ContactsInterface = ContactsInitialState,
    action
): ContactsInterface => {
    switch (action.type) {
        case ContactsActionTypes.CLEAR_UPLOAD_DATA:
            return {
                ...state,
                contactPattern: null,
                postContactsParams: {
                    listName: '0',
                    body: [],
                    status: ContactStatus.Active,
                    consentTracking: ConsentTracking.Unknown,
                },
            };
        case ContactsActionTypes.CREATE_WITH_PATTERN_START:
            return { ...state, isUploading: true };
        case ContactsActionTypes.CREATE_WITH_PATTERN_ERROR:
            return { ...state, isUploading: false };
        case ContactsActionTypes.FETCH_START:
        case ContactsActionTypes.DELETE_START:
            return { ...state, isContactsLoading: true };
        case ContactsActionTypes.FETCH_ERROR:
            return { ...state, isContactsLoading: false, collection: action.payload.collection };
        case ContactsActionTypes.FETCH_SUCCESS:
            return { ...state, ...action.payload };
        case ContactsActionTypes.FETCH_CONTACT_START:
        case ContactsActionTypes.FETCH_CONTACT_ERROR: {
            return { ...state, isContactsLoading: action.payload.isContactsLoading };
        }
        case ContactsActionTypes.FETCH_CONTACT_SUCCESS:
        case ContactsActionTypes.DELETE_SUCCESS:

        case ContactsActionTypes.FETCH_OVERVIEW_SUCCESS:
        case ContactsActionTypes.FETCH_NEW_SUCCESS:
            return { ...state, ...action.payload };
        case ContactsActionTypes.GET_HISTORY_START:
        case ContactsActionTypes.GET_HISTORY_ERROR:
        case ContactsActionTypes.GET_HISTORY_SUCCESS:
        case ContactsActionTypes.CREATE_WITH_PATTERN_SUCCESS:
        case ContactsActionTypes.UPDATE_SUCCESS:
        case ContactsActionTypes.FETCH_PATTERN_SUCCESS:
        case ContactsActionTypes.GET_GROWTH_SUCCESS:
        case ContactsActionTypes.SET_POST_PARAMS:
        case ContactsActionTypes.SET_POST_START:
            return {
                ...state,
                ...action.payload,
            };
        case ContactsActionTypes.CREATE_SINGLE:
        case ContactsActionTypes.CREATE_MULTIPLE:
            return {
                ...state,
                ...action.payload,
                isCreatingContacts: true,
            };
        case ContactsActionTypes.CREATE_SUCCESS:
            return {
                ...state,
                ...action.payload,
                isCreatingContacts: false,
            };
        case ContactsActionTypes.CREATE_ERROR:
            return {
                ...state,
                isCreatingContacts: false,
            };
        case ContactsActionTypes.CLEAR_CONTACTS_AND_COUNTS: {
            return { ...state, collection: null, counts: null };
        }
        case ContactsActionTypes.DESELECT:
            return { ...state, selectedContact: null, contactHistory: null };
        case ContactsActionTypes.GET_CONTACTS_UNSUBSCRIBED_OVERVIEW:
            return {
                ...state,
                unsubscribedContacts: { ...state.unsubscribedContacts, loadingUnsubscribedOverview: true },
            };
        case ContactsActionTypes.GET_CONTACTS_UNSUBSCRIBED_OVERVIEW_SUCCESS:
            return {
                ...state,
                unsubscribedContacts: {
                    ...state.unsubscribedContacts,
                    overview: action.payload.overview,
                    pagination: action.payload.pagination,
                    loadingUnsubscribedOverview: false,
                    unsubReasons: action.payload.unsubReasons,
                },
            };
        case ContactsActionTypes.GET_CONTACTS_UNSUBSCRIBED_OVERVIEW_ERROR:
            return {
                ...state,
                unsubscribedContacts: { ...state.unsubscribedContacts, loadingUnsubscribedOverview: false },
            };
        case ContactsActionTypes.GET_CONTACTS_UNSUBSCRIBED:
            return { ...state, unsubscribedContacts: { ...state.unsubscribedContacts, loadingUnsubscribed: true } };
        case ContactsActionTypes.GET_CONTACTS_UNSUBSCRIBED_SUCCESS:
            return {
                ...state,
                unsubscribedContacts: {
                    ...state.unsubscribedContacts,
                    contactsList: action.payload.contactsList,
                    statusCount: action.payload.statusCount,
                    pagination: action.payload.pagination,
                    loadingUnsubscribed: false,
                },
            };
        case ContactsActionTypes.GET_CONTACTS_UNSUBSCRIBED_ERROR:
            return { ...state, unsubscribedContacts: { ...state.unsubscribedContacts, loadingUnsubscribed: false } };
        case ContactsActionTypes.SET_PICKED_CONTACT_EMAIL:
            return {
                ...state,
                unsubscribedContacts: {
                    ...state.unsubscribedContacts,
                    pickedContactEmail: action.payload.pickedContactEmail,
                },
            };
        default:
            return state;
    }
};

export { reducer as contacts };

export default reducer;
